import AuthComponent from "@components/auth/component";
import CustomButton from "@components/buttons/CustomButton";
import ReportFilter from "@components/filters/ReportFilter";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import DeliveryOrderByCustomerTable from "@components/report/DeliveryOrderByCustomerTable";
import useGetAllIDeliveryOrderByCustomerReport from "@hooks/report/useGetAllIDeliveryOrderByCustomerReport";
import {
  useGetAllDeliveryNotesQuery,
  useLazyGetGoodsDeliveryQuery,
} from "@states/goods-request/delivery-note.api";
import {
  useGetAllGoodsRequestQuery,
  useLazyGetOneGoodsRequestQuery,
} from "@states/goods-request/goods-request.api";
import { useGetAllProductsQuery } from "@states/product/product.api";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import React, { useEffect, useRef, useState } from "react";
import { formatFilterDate } from "@utils/general/format-filter-date";
import { formatDBDate } from "@utils/general/format-db-date";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const getWayLabel = (order: any) =>
  order?.way_name || order?.delivery_way_name || order?.way?.way_name || "-";

const getDeliveryDateLabel = (order: any) => {
  const dateValue =
    order?.delivery_date || order?.delivery_complete_date || order?.issue_date;
  if (!dateValue || dateValue === "-") return "-";

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return formatDBDate(dateValue);
};

const getUnitLabel = (detail: any) =>
  detail?.unit_name ||
  detail?.base_unit_name ||
  detail?.unit?.unit_name ||
  detail?.unit ||
  "-";

const getQtyValue = (detail: any) =>
  Number(
    detail?.qty ??
      detail?.request_qty ??
      detail?.total_issued_qty ??
      detail?.issue_qty ??
      detail?.converted_qty ??
      detail?.base_qty ??
      0
  );

function DeliveryOrdersByCustomerReportPage() {
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const { search, setSearch } = useSearch();
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });

  const {
    inventoryReportError,
    inventoryReportRefetch,
    inventoryReportFetching,
    inventoryReportPaginator,
    inventoryReportData,
  } = useGetAllIDeliveryOrderByCustomerReport({
    //@ts-ignore
    from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
    //@ts-ignore
    to_date: filterRef.current?.to_date || formatFilterDate(today),
  });
  const { data: deliveryNotesData } = useGetAllDeliveryNotesQuery({
    page: 1,
    limit: 1000,
  });
  const { data: allProductsData } = useGetAllProductsQuery({
    page: 1,
    limit: 1000,
  });
  const { data: goodsRequestsData } = useGetAllGoodsRequestQuery({
    page: 1,
    limit: 1000,
  });
  const [getGoodsDelivery] = useLazyGetGoodsDeliveryQuery();
  const [getOneGoodsRequest] = useLazyGetOneGoodsRequestQuery();
  const [deliveryDetailLookup, setDeliveryDetailLookup] = useState<Map<string, any>>(
    new Map()
  );
  const [goodsRequestDetailLookup, setGoodsRequestDetailLookup] = useState<Map<string, any>>(
    new Map()
  );

  const deliveryNoteLookup = React.useMemo(() => {
    const lookup = new Map<string, any>();
    const notes = deliveryNotesData?.data || [];

    notes.forEach((note: any) => {
      if (note?.reference_id) {
        lookup.set(`ref:${note.reference_id}`, note);
      }
      if (note?.goods_request_note_code) {
        lookup.set(`req:${note.goods_request_note_code}`, note);
      }
      if (note?.goods_delivery_note_code) {
        lookup.set(`del:${note.goods_delivery_note_code}`, note);
      }
    });

    return lookup;
  }, [deliveryNotesData]);

  const productLookup = React.useMemo(() => {
    const lookup = new Map<string, any>();
    const products = allProductsData?.data || [];

    products.forEach((product: any) => {
      if (product?.product_id != null) {
        lookup.set(`id:${product.product_id}`, product);
      }
      const fullCode = product?.product_code_prefix
        ? `${product.product_code_prefix}${product.product_code || ""}`
        : product?.product_code;
      if (fullCode) {
        lookup.set(`code:${fullCode}`, product);
      }
    });

    return lookup;
  }, [allProductsData]);

  useEffect(() => {
    const loadGoodsRequestDetails = async () => {
      const reportData = inventoryReportData || [];
      const requests = goodsRequestsData?.data || [];

      if (!reportData.length || !requests.length) {
        setGoodsRequestDetailLookup(new Map());
        return;
      }

      const matchedIds = Array.from(
        new Set(
          reportData
            .map((order: any) => {
              const matchedRequest = requests.find(
                (request: any) =>
                  request.goods_request_note_code === order.goods_request_note_code
              );
              return matchedRequest?.goods_request_note_id;
            })
            .filter(Boolean)
        )
      );

      if (!matchedIds.length) {
        setGoodsRequestDetailLookup(new Map());
        return;
      }

      const detailResults = await Promise.all(
        matchedIds.map(async (goods_request_id: any) => {
          try {
            const response = await getOneGoodsRequest({ goods_request_id }).unwrap();
            return response?.data?.[0] || response?.data || null;
          } catch {
            return null;
          }
        })
      );

      const nextLookup = new Map<string, any>();

      detailResults.forEach((requestDetail: any) => {
        if (!requestDetail?.goods_request_note_code) return;
        nextLookup.set(`req:${requestDetail.goods_request_note_code}`, requestDetail);
      });

      setGoodsRequestDetailLookup(nextLookup);
    };

    loadGoodsRequestDetails();
  }, [inventoryReportData, goodsRequestsData, getOneGoodsRequest]);

  useEffect(() => {
    const loadDeliveryDetails = async () => {
      const reportData = inventoryReportData || [];
      const notes = deliveryNotesData?.data || [];

      if (!reportData.length || !notes.length) {
        setDeliveryDetailLookup(new Map());
        return;
      }

      const matchedIds = Array.from(
        new Set(
          reportData
            .map((order: any) => {
              const matchedNote = notes.find(
                (note: any) =>
                  note.reference_id === order.goods_request_note_code ||
                  note.goods_request_note_code === order.goods_request_note_code ||
                  note.goods_delivery_note_code === order.goods_request_note_code
              );
              return matchedNote?.goods_delivery_note_id;
            })
            .filter(Boolean)
        )
      );

      if (!matchedIds.length) {
        setDeliveryDetailLookup(new Map());
        return;
      }

      const detailResults = await Promise.all(
        matchedIds.map(async (goods_delivery_id: any) => {
          try {
            const response = await getGoodsDelivery({ goods_delivery_id }).unwrap();
            return response?.data;
          } catch {
            return null;
          }
        })
      );

      const nextLookup = new Map<string, any>();

      detailResults.forEach((delivery: any) => {
        if (!delivery) return;

        const referenceKeys = [
          delivery?.reference_id,
          delivery?.goods_request_note_code,
          delivery?.goods_delivery_note_code,
        ].filter(Boolean);

        referenceKeys.forEach((referenceKey: string) => {
          nextLookup.set(`ref:${referenceKey}`, delivery);
        });
      });

      setDeliveryDetailLookup(nextLookup);
    };

    loadDeliveryDetails();
  }, [inventoryReportData, deliveryNotesData, getGoodsDelivery]);

  const normalizedInventoryReportData = React.useMemo(() => {
    const reportData = inventoryReportData || [];

    return reportData.map((order: any) => {
      const matchedGoodsRequest =
        goodsRequestDetailLookup.get(`req:${order.goods_request_note_code}`);
      const matchedDeliveryNote =
        deliveryDetailLookup.get(`ref:${order.goods_request_note_code}`) ||
        deliveryNoteLookup.get(`ref:${order.goods_request_note_code}`) ||
        deliveryNoteLookup.get(`req:${order.goods_request_note_code}`) ||
        deliveryNoteLookup.get(`del:${order.goods_request_note_code}`);

      return {
        ...order,
        customer_name:
          order?.customer_name ||
          matchedGoodsRequest?.customer_first_name ||
          matchedGoodsRequest?.customer_name ||
          matchedDeliveryNote?.customer_name ||
          matchedDeliveryNote?.customer_first_name ||
          "Unknown Customer",
        business_unit_name:
          order?.business_unit_name ||
          matchedGoodsRequest?.business_unit_name ||
          matchedDeliveryNote?.business_unit_name ||
          "-",
        way_name:
          order?.way_name ||
          matchedDeliveryNote?.way_name ||
          matchedDeliveryNote?.delivery_way_name ||
          "-",
        delivery_date:
          order?.delivery_date ||
          matchedGoodsRequest?.delivery_date ||
          matchedDeliveryNote?.delivery_date ||
          matchedDeliveryNote?.delivery_complete_date ||
          matchedDeliveryNote?.delivery_note_date ||
          "-",
        detail: (order?.detail || []).map((detail: any) => {
          const fullCode = detail?.product_code_prefix
            ? `${detail.product_code_prefix}${detail.product_code || ""}`
            : detail?.product_code;
          const matchedDeliveryDetail = (matchedDeliveryNote?.product_detail || []).find(
            (deliveryDetail: any) => {
              const deliveryCode = deliveryDetail?.product_code_prefix
                ? `${deliveryDetail.product_code_prefix}${deliveryDetail.product_code || ""}`
                : deliveryDetail?.product_code;

              return (
                (detail?.product_id != null &&
                  deliveryDetail?.product_id != null &&
                  detail.product_id == deliveryDetail.product_id) ||
                (fullCode && deliveryCode && fullCode === deliveryCode) ||
                (detail?.product_name &&
                  deliveryDetail?.product_name &&
                  detail.product_name === deliveryDetail.product_name)
              );
            }
          );
          const matchedGoodsRequestDetail = (
            matchedGoodsRequest?.product_detail || []
          ).find((requestDetail: any) => {
            const requestCode = requestDetail?.product_code_prefix
              ? `${requestDetail.product_code_prefix}${requestDetail.product_code || ""}`
              : requestDetail?.product_code;

            return (
              (detail?.product_id != null &&
                requestDetail?.product_id != null &&
                detail.product_id == requestDetail.product_id) ||
              (fullCode && requestCode && fullCode === requestCode) ||
              (detail?.product_name &&
                requestDetail?.product_name &&
                detail.product_name === requestDetail.product_name)
            );
          });
          const matchedProduct =
            (detail?.product_id != null && productLookup.get(`id:${detail.product_id}`)) ||
            (fullCode && productLookup.get(`code:${fullCode}`));

          return {
            ...detail,
            unit_name:
              matchedGoodsRequestDetail?.unit_name ||
              matchedDeliveryDetail?.unit_name ||
              detail?.unit_name ||
              detail?.base_unit_name ||
              detail?.unit?.unit_name ||
              matchedProduct?.unit_name ||
              "-",
            qty:
              Number(
                matchedGoodsRequestDetail?.request_qty ??
                  matchedDeliveryDetail?.issue_qty ??
                  matchedDeliveryDetail?.request_qty
              ) ||
              getQtyValue(detail),
          };
        }),
      };
    });
  }, [
    inventoryReportData,
    goodsRequestDetailLookup,
    deliveryNoteLookup,
    deliveryDetailLookup,
    productLookup,
  ]);

  const handleExportClick = () => {
    const data = normalizedInventoryReportData || [];

    if (!data.length) {
      alert("No data to export");
      return;
    }

    const rows = data.flatMap((order: any) =>
      (order.detail || []).map((dtl: any) => ({
        Customer: order.customer_name || "-",
        "Business Unit": order.business_unit_name || "-",
        "Ref No": order.goods_request_note_code || "-",
        Way: getWayLabel(order),
        "Delivery Date": getDeliveryDateLabel(order),
        Status:
          order.status === 1 || order.status === "1"
            ? "Pending"
            : order.status === 2 || order.status === "2"
              ? "Delivered"
              : order.status || "-",
        "Product Code": dtl.product_code_prefix
          ? `${dtl.product_code_prefix}${dtl.product_code || ""}`
          : dtl.product_code || "-",
        "Product Name": dtl.product_name || "-",
        Unit: getUnitLabel(dtl),
        Qty: getQtyValue(dtl),
        "Allocation Account": dtl.warehouse_name || "-",
        "Sales Description": order.description || "-",
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "By Customer");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(blob, "DeliveryOrdersByCustomer.xlsx");
  };

  return (
    <AuthComponent
      action="view"
      module="Report"
      subModule="Delivery Orders By Customer"
      page
    >
      <>
        <Search setSearch={setSearch} />
        {inventoryReportFetching && <LoadingSpinner />}
        <PageWrapper title="Delivery Orders By Customer Report">
          <div className="grid grid-cols-8 justify-evenly">
            <div className="col-span-6">
              {/* @ts-ignore */}
              <ReportFilter
                refetch={inventoryReportRefetch}
                filterRef={filterRef}
              />
            </div>
            <div className=" flex items-center justify-end  mt-2 col-span-2">
              <CustomButton
                title={
                  <div className="flex  gap-3">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                        fill="white"
                      />
                      <path
                        d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                        fill="white"
                      />
                    </svg>
                    Export Excel
                  </div>
                }
                color="bg-[#1E6E42] h-3/6"
                handleClick={handleExportClick}
              />
            </div>
          </div>
          <DeliveryOrderByCustomerTable data={normalizedInventoryReportData} />
        </PageWrapper>
      </>
    </AuthComponent>
  );
}

export default DeliveryOrdersByCustomerReportPage;
